import {
  Anchor,
  Badge,
  Group,
  LoadingOverlay,
  Pagination,
  ScrollArea,
  Select,
  Table,
} from "@mantine/core";
import { DateField, List, ShowButton } from "@refinedev/mantine";
import { useTable } from "@refinedev/react-table";
import { ColumnDef, Header } from "@tanstack/react-table";
import AppliedFilters from "components/common/list/AppliedFilters";
import Headers from "components/common/list/Headers";
import { Rows } from "components/common/list/Rows";
import { Invoice } from "entities/invoice";
import { colorsBySource, selectSources } from "entities/invoice/source";
import { colorsByStatus, selectStatuses } from "entities/invoice/status";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { getInvoiceUrl } from "utils/chargebee";

export const InvoiceList: React.FC = () => {
  const columns = useMemo<ColumnDef<Invoice>[]>(
    () => [
      {
        id: "id",
        header: "ID",
        accessorKey: "id",
      },
      {
        id: "remote_id",
        header: "Remote ID",
        accessorKey: "remote_id",
        cell: function render({ getValue }) {
          const remoteId = getValue<Invoice["remote_id"]>();

          return <Anchor href={getInvoiceUrl(remoteId)}>{remoteId}</Anchor>;
        },
      },
      {
        id: "user",
        header: "User",
        accessorKey: "user",
        cell: function render({ getValue }) {
          const user = getValue<Invoice["user"]>();

          return (
            <Anchor component={Link} to={`/users/show/${user?.id}`}>
              {user?.email}
            </Anchor>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "status",
        cell: function render({ getValue }) {
          const status = getValue<Invoice["status"]>();

          return <Badge color={colorsByStatus[status]}>{status}</Badge>;
        },
        meta: {
          filterElement: ({ header }: { header: Header<Invoice, any> }) => {
            return (
              <div>
                <Select
                  placeholder="Status"
                  data={selectStatuses}
                  onChange={header.column.setFilterValue}
                  clearable
                />
              </div>
            );
          },
        },
      },
      {
        id: "source",
        header: "Source",
        accessorKey: "source",
        cell: function render({ getValue }) {
          const source = getValue<Invoice["source"]>();

          return <Badge color={colorsBySource[source]}>{source}</Badge>;
        },
        meta: {
          filterElement: ({ header }: { header: Header<Invoice, any> }) => {
            return (
              <div>
                <Select
                  placeholder="Source"
                  data={selectSources}
                  onChange={header.column.setFilterValue}
                  clearable
                />
              </div>
            );
          },
        },
      },
      {
        id: "subscription",
        header: "Subscription",
        accessorKey: "subscription",
        cell: function render({ getValue }) {
          const subscription = getValue<Invoice["subscription"]>();
          if (!subscription) return <div />;

          return (
            <Anchor
              component={Link}
              to={`/subscriptions/show/${subscription.id}`}
            >
              {subscription.remote_id}
            </Anchor>
          );
        },
      },
      {
        id: "paid_at",
        header: "Paid at",
        accessorKey: "paid_at",
        cell: function render({ getValue }) {
          const paidAt = getValue<Invoice["paid_at"]>();
          if (!paidAt) return <div />;

          return <DateField value={paidAt} format="DD/MM/YYYY HH:mm:ss" />;
        },
      },
      {
        id: "actions",
        header: "Actions",
        accessorKey: "id",
        cell: function render({ getValue }) {
          return (
            <Group spacing="xs" noWrap>
              <ShowButton hideText recordItemId={getValue() as number} />
            </Group>
          );
        },
      },
    ],
    [],
  );

  const {
    getHeaderGroups,
    getRowModel,
    setOptions,
    getFlatHeaders,
    refineCore: {
      setCurrent,
      pageCount,
      current,
      tableQueryResult: { data: tableData, isFetching },
    },
  } = useTable({
    columns,
    refineCoreProps: {
      sorters: {
        initial: [
          {
            field: "id",
            order: "desc",
          },
        ],
      },
      pagination: {
        pageSize: 50,
      },
    },
  });

  return (
    <List>
      <AppliedFilters<Invoice> headers={getFlatHeaders()} />
      <ScrollArea>
        <Table highlightOnHover pos="relative">
          <LoadingOverlay visible={isFetching} />
          <Headers<Invoice> headerGroups={getHeaderGroups()} />
          <Rows<Invoice> rows={getRowModel().rows} />
        </Table>
      </ScrollArea>
      <br />
      <Pagination
        position="right"
        total={pageCount}
        page={current}
        onChange={setCurrent}
      />
    </List>
  );
};
